import { Stack, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { MailrailBaseEmailCampaign, PinpointEmailBaseCampaign } from '@feathr/blackbox';
import { Alert, Checkbox, ConfirmModal, Spinner, Text } from '@feathr/components';

import AudienceDescription from './AudienceDescription';
import { useEmailAudienceData } from './useEmailAudienceData';

interface IEmailAudienceConfirmModalProps {
  campaign: MailrailBaseEmailCampaign | PinpointEmailBaseCampaign;
  opened: boolean;
  onConfirm: () => Promise<void> | void;
  onClose: () => void;
}

function EmailAudienceConfirmModal({
  campaign,
  opened,
  onConfirm,
  onClose,
}: Readonly<IEmailAudienceConfirmModalProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const [isConfirmed, setIsConfirmed] = useState(false);
  const { uniqueEmailCount, sampleEmails, isLoading, error } = useEmailAudienceData({
    campaign,
    isOpen: opened,
  });

  useEffect(() => {
    if (opened) {
      setIsConfirmed(false);
    }
  }, [opened]);

  function handleCheckboxChange(newValue?: boolean): void {
    setIsConfirmed(!!newValue);
  }

  const isPublishDisabled = !isConfirmed || isLoading || uniqueEmailCount === undefined;

  let audienceContent: JSX.Element | false = false;
  if (isLoading) {
    audienceContent = <Spinner />;
  } else if (error) {
    audienceContent = (
      <Alert
        description={t('Could not load audience data. Close and try again.')}
        title={t('Error')}
        type={'danger'}
      />
    );
  } else if (uniqueEmailCount !== undefined) {
    audienceContent = (
      <Stack gap={theme.spacing['2']}>
        <AudienceDescription audienceCount={uniqueEmailCount} sampleEmails={sampleEmails} />
        <Text.DefaultRegular truncate={false}>
          {t('Confirm this is the correct audience before publishing.')}
        </Text.DefaultRegular>
      </Stack>
    );
  }

  return (
    <ConfirmModal
      confirmButtonText={t('Publish campaign')}
      confirmButtonType={'primary'}
      confirmDisabled={isPublishDisabled}
      onClose={onClose}
      onConfirm={onConfirm}
      opened={opened}
      t={t}
      title={t('Confirm email audience')}
    >
      <Stack gap={theme.spacing['4']}>
        {audienceContent}
        {!error && (
          <Alert
            description={t(
              'Audience filters may have changed since you last reviewed. Verify the count is correct.',
            )}
            title={t('Important reminder')}
            type={'info'}
          />
        )}
        <Checkbox
          label={t('I confirm this audience is correct')}
          onChange={handleCheckboxChange}
          value={isConfirmed}
        />
      </Stack>
    </ConfirmModal>
  );
}

export default observer(EmailAudienceConfirmModal);
